import { useState, useEffect } from "react";
import { findTheory, PROBLEM_GROUPS, SOLUTION_GROUPS, KIND } from "./theories";
import { findFeeling } from "./learnContent";

// Where you are inside Learn, as a stack of views ("home", "why", "detail:p1", …).
// Kept in sessionStorage so that coming back from Chat ("Talk it through")
// returns you to the page you left; a new session starts at the Learn home.
export const VIEWS = ["home", "foundations", "why", "how", "stuck", "research"];

const storageKey = (uid) => `nudge-learn-ui-${uid}`;

export const detailView = (kind, n) => `detail:${kind}${n}`;

// "detail:p12" → { kind: "p", n: 12 }; any other view → null.
export const parseDetail = (view) => {
  const m = /^detail:([ps])(\d+)$/.exec(view);
  return m ? { kind: m[1], n: Number(m[2]) } : null;
};

// "I'm stuck right now" → one feeling's page: "feeling:tired".
const FEELING = "feeling:";
export const feelingView = (id) => FEELING + id;
export const parseFeeling = (view) => (view.startsWith(FEELING) ? findFeeling(view.slice(FEELING.length)) : null);

const isKnownView = (view) => {
  if (VIEWS.includes(view) || parseFeeling(view)) return true;
  const d = parseDetail(view);
  return !!d && !!findTheory(d.kind, d.n);
};

// A saved stack is used only if every entry still exists and it starts at home.
export const loadStack = (uid) => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(storageKey(uid)) || "null");
    if (Array.isArray(saved) && saved[0] === "home" && saved.every(isKnownView)) return saved;
  } catch (e) { /* bad or blocked storage: start fresh */ }
  return ["home"];
};

// Which groups are open on a list (reasons or strategies), kept for the session like the
// page stack, so coming back from a theory, or from Chat, finds the list as you left it.
const groupsKey = (uid, kind) => `nudge-learn-groups-${kind}-${uid}`;
const PRINCIPLES = {
  [KIND.PROBLEM]: PROBLEM_GROUPS.map(g => g.principle),
  [KIND.SOLUTION]: SOLUTION_GROUPS.map(g => g.principle),
};

export function useOpenGroups(uid, kind) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(groupsKey(uid, kind)) || "[]");
      return Array.isArray(saved) ? saved.filter(p => PRINCIPLES[kind].includes(p)) : [];
    } catch (e) { return []; /* bad or blocked storage: start closed */ }
  });
  useEffect(() => {
    try { sessionStorage.setItem(groupsKey(uid, kind), JSON.stringify(open)); } catch (e) { /* private mode: groups still open and close */ }
  }, [uid, kind, open]);
  const toggle = (p) => setOpen(o => (o.includes(p) ? o.filter(x => x !== p) : [...o, p]));
  return [open, toggle];
}

export function useLearnNav(uid) {
  const [stack, setStack] = useState(() => loadStack(uid));

  useEffect(() => {
    try { sessionStorage.setItem(storageKey(uid), JSON.stringify(stack)); } catch (e) { /* private mode: navigation still works */ }
  }, [uid, stack]);

  return {
    view: stack[stack.length - 1],
    depth: stack.length,
    isHome: stack.length === 1,
    // Opening the page you're already on adds nothing to the way back.
    open: (view) => setStack(s => (s[s.length - 1] === view ? s : [...s, view])),
    back: () => setStack(s => (s.length > 1 ? s.slice(0, -1) : s)),
    home: () => setStack(["home"]),
  };
}
